// This is used as a base for both applications.

import { useVuelidate } from "@vuelidate/core";
import { ref, reactive, toRef, computed } from "vue";
import axios from "axios";
import { useFavouritesStore } from "../store/index";

import globals from "../../lib/globals";
import unique from "../../lib/unique";
import applicationReset from "../../lib/applicationReset";
import utils from "./utils";
import { extendApplicantCreator } from "../../lib/applicationShared";
import ApplicationBase from "../components/ApplicationBase";
import ApplicationApartments from "../components/ApplicationApartments";
import ApplicationApplicants from "../components/ApplicationApplicants";
import ApplicationSection from "../components/ApplicationSection";
import ApplicationDivider from "../components/ApplicationDivider";
import ApplicationOtherResidents from "../components/ApplicationOtherResidents";
import InputWrap from "../components/InputWrap";
import InputText from "../components/InputText";
import InputCheckbox from "../components/InputCheckbox";
import InputDate from "../components/InputDate";
import InputWrapDate from "../components/InputWrapDate";
import InputSelect from "../components/InputSelect";
import InputTextarea from "../components/InputTextarea";
import { updateCookieWithString } from "../../lib/cookieUtils";
import { createURLWithParams } from "../../lib/urlUtils";
import { maybeAddQueryParam } from "../../lib/urlUtils";

// Helper for calling extend stuff (that may or may not be defined).
const createExtend =
  (extensions) =>
  (name, ...args) =>
    extensions.hasOwnProperty(name) ? extensions[name](...args) : {};
let hasListeners = false;

export default ({
  favouritesType,
  presetMapExtend,
  localSaveExtend,
  applyOtherData,
}) => ({
  mixins: [utils.translate],
  // NOTE: setup() is NOT supported in mixins in Vue 3!
  // We initialize Vuelidate in the created() hook instead.
  components: {
    InputSelect,
    InputCheckbox,
    InputWrap,
    InputTextarea,
    InputText,
    InputDate,
    InputWrapDate,
    ApplicationOtherResidents,
    ApplicationApplicants,
    ApplicationApartments,
    ApplicationSection,
    ApplicationDivider,
    ApplicationBase,
  },
  props: {
    heroImage: {
      type: String,
      required: true,
    },
    heroes: {
      type: Object,
      required: true,
    },
    textBefore: {
      type: String,
      default: "",
    },
    textAfter: {
      type: String,
      default: "",
    },
    warningApartmentsBefore: {
      type: String,
      default: "",
    },
    warningApartmentsAfter: {
      type: String,
      default: "",
    },
    successContent: {
      type: String,
      default: "",
    },
    successContentApply: {
      type: String,
      default: "",
    },
    modalLoadPresetEnabled: {
      type: Boolean,
      default: false,
    },
    modalLoadPresetTitle: {
      type: String,
      default: "",
    },
    modalLoadPresetContent: {
      type: String,
      default: "",
    },
    modalLoadPresetClose: {
      type: String,
      default: "",
    },
    preset: {
      type: Object,
      default: null,
    },
    disableFormFromWordPress: {
      type: Boolean,
      default: false,
    },
    ssn: {
        type: String,
        default: '',
    },
    applicationId: {
        type: String,
        default: '',
    },
    eidsNoApplicationTitle: {
      type: String,
      default: '',
    },
    eidsNoApplicationMessage: {
      type: String,
      default: '',
    },
    eidsNoApplicationLinkTitle: {
      type: String,
      default: '',
    },
    eidsNoApplicationLinkUrl: {
      type: String,
      default: '',
    },
    hash: {
      type: String,
      default: '',
    },
    hash2: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      // Vuelidate validation object - initialized in created() hook
      v$: null,
      maxApartments: 100, // dunno
      enableLocalStorageUpdate: false,
      view: "form",
      submitting: false,
      deactivating: false,
      submitError: false,
      deactivateError: false,
      showModal: {
        loadPreset: false,
        confirmReset: false,
        prefillApplication: false,
        confirmDeactivation: false,
      },
      modalLoading: {
        prefillApplication: false,
      },
      modalError: {
        prefillApplication: false,
      },
      modalErrorMessages: {
        prefillApplication: "",
      },
      appliedApartments: [],
      applicants: [this.createApplicant()],
      existingApplicantIndexes: {},
      /** Snapshot count when state last matched server/local save; new rows at higher indexes get AddedNewApplicant. */
      initialApplicantsCount: 1,
      /**
       * Component data() replaces the mixin `form` object entirely (Vue shallow merge);
       * include the same base keys as mixins/application.js so OtherResidents and ARA fields exist.
       */
      form: {
        Id: null,
        OtherResidents: [],
        AdditionalWishes: "",
        ASOAraOrderNumber: "",
        isARA: false,
        ARACurrentAccommodationType: "",
        ARACurrentMunicipality: "",
        Checksum: "",
      },
      residentInLocation: {
        localityId: "",
        isResident: false,
        localityName: [],
      },
      numberOfGroupedLocalities: 0,
      numberOfGroupedLocalitiesByCity: {},
      /** Set true after failed preview/submit so InputWrap shows errors without per-field blur. */
      formValidationContext: {
        submitAttempted: false,
      },
    };
  },
  provide() {
    return {
      formValidationContext: this.formValidationContext,
    };
  },
  // Initialize Vuelidate in created() since setup() doesn't work in mixins
  created() {
    console.log('[APPLICATION-DEBUG] created() hook called - initializing Vuelidate');
    
    // Get the validations from the component options
    const validationsFn = this.$options.validations;
    console.log('[APPLICATION-DEBUG] validations type:', typeof validationsFn);
    
    if (typeof validationsFn === 'function') {
      // Wrap rules in a computed so they react to data changes
      // This is critical for helpers.forEach to properly validate array items
      const vm = this;
      const rules = computed(() => validationsFn.call(vm));
      console.log('[APPLICATION-DEBUG] rules keys:', Object.keys(rules.value));
      
      try {
        // useVuelidate can be called outside setup() when providing explicit rules and state
        // Pass the computed rules so validation reacts to data changes
        const result = useVuelidate(rules, this.$data);
        // Store the ref so we can access .value reactively
        this._v$Ref = result;
        // Also set v$ to the current value for backwards compatibility
        this.v$ = result.value;
      } catch (error) {
        console.error('[APPLICATION-DEBUG] Error initializing Vuelidate:', error);
      }
    } else if (typeof validationsFn === 'object' && validationsFn !== null) {
      try {
        const result = useVuelidate(validationsFn, this.$data);
        this._v$Ref = result;
        this.v$ = result.value;
      } catch (error) {
        console.error('[APPLICATION-DEBUG] Error initializing Vuelidate:', error);
      }
    } else {
      console.error('[APPLICATION-DEBUG] No validations found on component!');
    }
  },
  computed: {
    store() {
      return useFavouritesStore();
    },
    favourites() {
      return this.store.favourites[favouritesType];
    },
    apartments() {
      return this.store.favouritesData[favouritesType];
    },
    loadingApartments() {
      return !!this.store.favouritesDataLoading;
    },
    vBindBase() {
      return {
        hero: {
          ...this.heroes[this.view],
          image: this.heroImage,
        },
        textBefore: this.textBefore,
        textAfter: this.textAfter,
        warningApartmentsBefore: this.warningApartmentsBefore,
        success: {
          content: this.view === 'deactivation-success' ? this.deactiovationSuccessContent : this.successContent,
          applyOther: {
            show:
              this.store.favourites[
                applyOtherData.conditionFavouritesType
              ].length > 0,
            title: applyOtherData.title,
            content: this.view === 'deactivation-success' ? this.deactiovationSuccessContentApply : this.successContentApply,
            ctaText: applyOtherData.ctaText,
            color: applyOtherData.color,
          },
        },
        view: this.view,
        // useVuelidate returns a ref; read .value so ApplicationBase sees live $invalid (not a created()-time snapshot).
        validations: this._v$Ref?.value ?? this.v$ ?? {},
        submitting: this.submitting,
        deactivating: this.deactivating,
        loading: this.loadingApartments || !this.canRenderForm, // Also show loading if form not ready
        submitError: this.submitError,
        deactivateError: this.deactivateError,
        hasApartments: this.apartments.length > 0,
        isExistingApplication: this.isExistingApplication,
        existingApplicantIndexes: this.existingApplicantIndexes,
        canRenderForm: this.canRenderForm, // Pass this flag to child components
        formSubmitAttempted: this.formValidationContext.submitAttempted,
        modals: {
          loadPreset: {
            show: this.showModal.loadPreset,
            title: this.modalLoadPresetTitle,
            content: this.modalLoadPresetContent,
            textClose: this.modalLoadPresetClose,
          },
          confirmReset: {
            show: this.showModal.confirmReset,
          },
          prefillApplication: {
            show: this.showModal.prefillApplication,
            loading: this.modalLoading.prefillApplication,
            error: this.modalError.prefillApplication,
            errorMessage: this.modalErrorMessages.prefillApplication,
          },
          confirmDeactivation: {
            show: this.showModal.confirmDeactivation,
          },
        },
        loadApplicationDataFromAPI: this.loadApplicationDataFromAPI,
        disableFormFromWordPress: this.disableFormFromWordPress,
        ssn: this.ssn,
        applicationId: this.applicationId,

        /**
         * eidsNoApplication contents.
         */
        eidsNoApplicationTitle: this.eidsNoApplicationTitle,
        eidsNoApplicationMessage: this.eidsNoApplicationMessage,
        eidsNoApplicationLinkTitle: this.eidsNoApplicationLinkTitle,
        eidsNoApplicationLinkUrl: this.eidsNoApplicationLinkUrl,
        logoutUser: this.logoutUser,
        hash: this.hash,
        hash2: this.hash2,
      };
    },
    /**
     * Listens to the events move and ShowModal
     *
     * @returns {{move: *, showModal: *}}
     */
    vOnBase() {
      return {
        move: this.move,
        showModal: (modal, value) => {
          this.showModal[modal] = value;
        },
      };
    },
    vBindApartments() {
      return {
        interactive: this.view === "form",
        loading: this.loadingApartments,
        apartments:
          this.view === "success" ? this.appliedApartments : this.apartments,
        apartmentsCountMax: this.maxApartments,
        warningApartmentsAfter: this.warningApartmentsAfter,
        residentInLocation: this.residentInLocation,
      };
    },
    vOnApartments() {
      const remove = (uuid) => {
        this.store.removeFavourite({ uuid, type: favouritesType });
      };
      const selectedCheckbox = (obj) => {
        this.residentInLocation = {
          isResident: obj.value.length > 0,
          localityId: obj.localityId,
          localityName: obj.value,
        };
      };
      const groupedLocalities = (value) => {
        this.numberOfGroupedLocalities = value;
      };
      const groupedLocalitiesByCity = (value) => {
        this.numberOfGroupedLocalitiesByCity = value;
      };
      return {
        remove,
        selectedCheckbox,
        groupedLocalities,
        groupedLocalitiesByCity,
      };
    },
    /**
     * Provides validation state for applicants array fields.
     * This is here because the validation objects are not
     * reactive when passed to the applicants component.
     *
     * Uses Vuelidate v2 helpers.forEach pattern:
     * - $response.$data[index] contains validation results per field
     * - v-model binds to item data directly
     * - Pass validation objects directly (no wrapper) to preserve reactivity
     * @see https://vuelidate-next.netlify.app/examples.html
     */
    vBindApplicants() {
      let vIter = {};

      try {
        const v$ = this._v$Ref?.value || this.v$;
        if (v$ && v$.applicants && v$.applicants.$each) {
          const response = v$.applicants.$each.$response;
          
          // $response.$data can be an array-like object, not a true Array
          if (response && response.$data) {
            const dataArray = response.$data;

            // Indices are the indexes of the applicants.
            const indices = Object.keys(dataArray).filter(k => !isNaN(parseInt(k)));

            indices.forEach((indexStr) => {
              const index = parseInt(indexStr);
              const validationResult = dataArray[index];
              if (!validationResult) return;
              
              // Pass validation objects directly to preserve Vue's reactivity tracking
              vIter[index] = validationResult;
            });
          }
        }
      } catch (e) {
        console.error('[APPLICATION-DEBUG] Error in vBindApplicants:', e);
      }

      return {
        items: this.applicants || [],
        itemInit: this.createApplicant,
        vIter,
        preview: this.preview,
        isExistingApplication: this.isExistingApplication,
        existingApplicantIndexes: this.existingApplicantIndexes,
        initialApplicantsCount: this.initialApplicantsCount,
      };
    },
    computedLocalSave() {
      return this.getLocalSaveFromState(true);
    },
    isExistingApplication() {
      return !!this.form.Id;
    },
    preview() {
      return this.view === "preview";
    },
    /**
     * Provides validation state for OtherResidents array fields.
     * Pass validation objects directly to preserve Vue's reactivity tracking.
     * @see https://vuelidate-next.netlify.app/examples.html
     */
    vFormOtherResidentsIter() {
      let vIter = {};
      
      try {
        const v$ = this._v$Ref?.value || this.v$;
        if (v$ && v$.form && v$.form.OtherResidents && v$.form.OtherResidents.$each) {
          const response = v$.form.OtherResidents.$each.$response;
          
          // $response.$data can be an array-like object, not a true Array
          if (response && response.$data) {
            const dataArray = response.$data;
            const indices = Object.keys(dataArray).filter(k => !isNaN(parseInt(k)));
            
            indices.forEach((indexStr) => {
              const index = parseInt(indexStr);
              const validationResult = dataArray[index];
              if (!validationResult) return;
              
              // Pass validation objects directly to preserve Vue's reactivity tracking
              vIter[index] = validationResult;
            });
          }
        }
      } catch (e) {
        console.error('[APPLICATION-DEBUG] Error in vFormOtherResidentsIter:', e);
      }
      
      return vIter;
    },
    // Check if vuelidate is ready - this is critical for determining if the form can be shown
    isVuelidateReady() {
      const v$ = this._v$Ref?.value ?? this.v$;
      return !!(v$ && v$.applicants && v$.applicants.$each);
    },
    // Check if the form can be safely rendered (all required data initialized)
    canRenderForm() {
      const hasApplicants = Array.isArray(this.applicants) && this.applicants.length > 0;
      const hasV$ = this.isVuelidateReady;
      return hasApplicants && hasV$;
    },
    // This is used to ensure that there is only one occurrence of each ssn.
    allSsns() {
      // Guard against Vue 3 initialization order issues
      if (!this.applicants || !this.form || !this.form.OtherResidents) {
        return [];
      }
      return [
        ...this.applicants.map((item) => item.Ssn),
        ...this.form.OtherResidents.map((item) => item.Ssn),
      ];
    },
  },
  watch: {
    // Watch favourites deeply to detect array mutations (push/splice)
    favourites: {
      handler() {
        this.store.requestAndCleanUpFavourites();
      },
      deep: true,
    },
    // Copy the state of computedLocalSave to local storage as it changes.
    computedLocalSave: {
      handler(newLocalSave) {
        // Guard against null during initialization
        if (this.enableLocalStorageUpdate && newLocalSave) {
          this.store.storeLocalSave({
            type: favouritesType,
            localSave: newLocalSave,
          });
        }
      },
      deep: true,
    },
  },
  methods: {
    /**
     * Fetches the application data from the Laravel API
     * (.. which calls the Node proxy, which then calls the Avain CRM).
     * Uses api.php route `/public/application/'
     *
     * @return void
     */
    async loadApplicationDataFromAPI(applicationType) {

      this.modalLoading.prefillApplication = true;

      let res;

      try {
        res = (
          await axios.get(
            globals.urlIntegrationApi +
            `/public/application/?hash=${this.hash}&hash2=${this.hash2}&application_type=${applicationType}`
          )
        ).data;
      }
      catch (error) {
        this.modalLoading.prefillApplication = false;
        console.error( 'API request failed' );
      }
      console.log( res);
      /**
       * If the SSN matches with the applicant SSN, the application data is returned in the response. We then need to
       * reset the state and the localStorage and map the application data to them again.
       */
      if (res.success && res.success === true) {
        this.modalError.prefillApplication = false;
        this.modalErrorMessages.prefillApplication = "";
        window.localStorage.clear();

        await this.populateStateAndLocalStorageWithApplicationData(
          res.applicationData,
          applicationType
        );
        this.showModal.prefillApplication = false;

        // Vuelidate-next: per-row validators live under $each.$response.$data (same as vBindApplicants).
        await this.$nextTick();
        this.existingApplicantIndexes = {};
        const v$Loaded = this._v$Ref?.value || this.v$;
        const applicantsEachResponse = v$Loaded?.applicants?.$each?.$response;
        const applicantValidators = applicantsEachResponse?.$data;
        if (applicantValidators) {
          const indices = Object.keys(applicantValidators).filter((k) =>
            !Number.isNaN(Number.parseInt(k, 10))
          );
          indices.forEach((indexStr) => {
            const index = Number.parseInt(indexStr, 10);
            const applicantValidator = applicantValidators[index];
            const ssnModel = applicantValidator?.Ssn?.$model;
            if (ssnModel !== undefined && ssnModel !== '') {
              this.existingApplicantIndexes[index] = true;
            }
          });
        }

        // After successful processing, return the success object
        return { success: true };
      }

      /**
       * If the SSN does not match with the applicant SSN, display the error message.
       */
      this.modalError.prefillApplication = true;
      this.modalErrorMessages.prefillApplication = res.message;

      // If the API call succeeded but 'res.success' was false or missing, return the response object.
      // The external .then() will then check the 'success' flag.
      return res;
    },
    navigateAfterReset() {
      window.location.href = globals.urlHome;
    },
    move(to) {
      if (to === "preview") {
        const v$Preview = this._v$Ref?.value ?? this.v$;
        if (v$Preview && v$Preview.$invalid) {
          this.formValidationContext.submitAttempted = true;
          v$Preview.$touch();
          return;
        }
        this.formValidationContext.submitAttempted = false;
        window.scrollTo(0, 0);
        this.view = "preview";
      } else if (to === "back") {
        this.formValidationContext.submitAttempted = false;
        window.scrollTo(0, 0);
        this.view = "form";
      } else if (to === "submit") {
        const v$Submit = this._v$Ref?.value ?? this.v$;
        if (v$Submit && v$Submit.$invalid) {
          this.formValidationContext.submitAttempted = true;
          v$Submit.$touch();
          this.$nextTick(() => {
            const v$AfterTouch = this._v$Ref?.value ?? this.v$;
            if (v$AfterTouch && !v$AfterTouch.$invalid) {
              this.submit();
              return;
            }
            const firstError = document.querySelector('.input-wrap--error');
            if (firstError) {
              firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          });
          return;
        }
        //Disabling order number check since the fields have been set to be required
        // when needed this.checkOrderNumbers();
        this.submit();
      } else if (to === "reset") {

        // If we are editing the old application:
        // We have OIDC session active AND
        // we need to redirect to logout from the OIDC.
        if ( this.isExistingApplication ) {

          this.updateRefererCookie( 'logged-out' );

          this.logoutUser();
        } else {

          // If we are handling new application.
          this.resetLocalStorage();
          this.navigateAfterReset();
        }
      } else if (to === "deactivateApplication") {

        this.deactivateApplication();

        this.updateRefererCookie( 'deactivated' );

        this.logoutUser();

      } else if (to === "deactivationSuccess") {
        window.scrollTo(0, 0);
        this.view = "deactivation-success";
      } else if (to === "success") {
        window.scrollTo(0, 0);
        this.view = "success";
      } else if (to === "applyOther") {
        const state = this.getLocalSaveFromState();
        localStorage.setItem("applicationShared", JSON.stringify(state));
        window.location.href = applyOtherData.ctaUrl;
      }
    },
    orderNumberModalHandler() {
      // This is hacky but still better than using "prompt".
      // The eventlisteners are not removed, which is not optimal.

      this.modal = document.getElementsByClassName("ordernumber-modal");
      this.sendButton = document.getElementById("application-send");
      this.cancelButton = document.getElementById("application-cancel");

      if (this.modal.length > 0) {
        this.modal = this.modal[0];
      } else {
        return;
      }
      this.modal.classList.add("visible");
      if (!hasListeners) {
        this.sendButton.addEventListener("click", () => this.numberModalSend());
        this.cancelButton.addEventListener("click", () =>
          this.numberModalCancel()
        );
        hasListeners = true;
      }
    },
    numberModalSend() {
      this.modal.classList.remove("visible");
      this.submit();
    },
    numberModalCancel() {
      this.modal.classList.remove("visible");
    },
    checkOrderNumbers() {
      // Check if there is order number for every city and inform user.
      let missingOrderNumber = this.apartments
        .map(this.createFormApartment)
        .find((apartment) => apartment.ASOOrderNumber == "");

      if (missingOrderNumber === undefined || this.form.isARA) {
        this.submit();
      } else {
        this.orderNumberModalHandler();
      }
    },
    async deactivateApplication() {
      this.deactivating = true;
      this.deactivateError = false;
      // When deactivating only Type and application Id need to have input
      // other fields are required (or Digia suspects this) to exists
      const payload = {
        Type: "4", // 4 === deactivate value.
        TotalWealth: null,
        ApplicationReason: null,
        NeedForApartmentStartDate: null,
        ApplicationReasonAdditionalInfo: null,
        CurrentApartmentInfo: {
          AccommodationType: null,
          ApartmentType: null,
          Area: null,
          NumberOfResidents: null,
          Rent: null,
          MaintenanceCharge: null
        },
        Id: this.form.Id,
        OtherResidents: [],
        AdditionalWishes: null,
        ASOAraOrderNumber: null,
        isARA: "false",
        ARACurrentAccommodationType: null,
        ARACurrentMunicipality: null,
        // TR556: Send the original checksum so the server can verify that
        // the application data (Id + SSNs) hasn't been tampered with.
        // Previously null, which bypassed server-side integrity validation.
        Checksum: this.form.Checksum,
        SelectedApartments: [
          {
            Id: "b5a0ca3f-c127-446b-bc63-e9889d510378", // Dummy uuid (the field is required)
          }
        ],
        PrimaryApplicant: {
          FirstName: "-",
          LastName: "-",
          // TR556: Send the real SSN instead of "-" so the server-side
          // checksum recalculation (SALT + Id + SSNs) matches the original.
          Ssn: this.applicants[0].Ssn,
          Address: null,
          PostalCode: null,
          City: null,
          usePrimaryApplicantAddress: null,
          Phone: null,
          PhoneCountryCode: null,
          ContactLanguage: null,
          Email: null,
          emailAgain: null,
          MarketingAllowed: "false",
          GrossIncome: null,
          Occupation: null,
          Employer: null,
          JobStartDate: null
        },
        // TR556: Include the second applicant's real SSN if one exists, since
        // the checksum was generated with it. Other fields use dummy values
        // as they are not part of the checksum calculation.
        OtherApplicants: this.applicants.length > 1
          ? [{ Ssn: this.applicants[1].Ssn, FirstName: "-", LastName: "-", MarketingAllowed: "false" }]
          : [],
        IncomeArray: [
          {
            PrimaryApplicant: null
          }
        ]
      };
      console.log(payload);

      try {
        await axios.post(
          globals.urlIntegrationApi + "/public/application/process/",
          payload
        );
      } catch (error) {
        console.log("Caught error: ", error);
        this.deactivateError = true;
        this.deactivating = false;
        return;
      }

      // Done deactivating, reset the state.
      this.deactivating = false;

      // Favourites and fields are cleared when application is submitted.
      this.appliedApartments = this.apartments;

      this.move("deactivationSuccess");
    },
    async submit() {
      this.formValidationContext.submitAttempted = false;
      console.log("submit");
      const primaryApplicant = this.applicants[0];
      const applicants = this.applicants.map((applicant) => ({
        ...applicant,
        ...(applicant.usePrimaryApplicantAddress
          ? {
              Address: primaryApplicant.Address,
              PostalCode: primaryApplicant.PostalCode,
              City: primaryApplicant.City,
            }
          : {}),
        Ssn: applicant.Ssn.toUpperCase(),
        MarketingAllowed: applicant.MarketingAllowed ? "true" : "false",
      }));

      const payload = {
        ...this.form,
        isARA: this.form.isARA ? "true" : "false",
        SelectedApartments: this.apartments.map(this.createFormApartment),
        PrimaryApplicant: applicants.shift(),
        OtherApplicants: applicants,
      };

      this.submitting = true;
      this.submitError = false;

      try {
        await axios.post(
          globals.urlIntegrationApi + "/public/application/process/",
          payload
        );
      } catch (error) {
        this.submitError = true;
        this.submitting = false;
        return;
      }
      this.submitting = false;

      this.appliedApartments = this.apartments;

      if ( this.isExistingApplication ) {

        /**
         * If user has navigated to different view from the application view
         * the ?application param will be lost.
         * Here we add application param if needed.
         */
        const applicationUrlWithApplicationParam = maybeAddQueryParam(
          window.location.href,
          'application',
          this.form.Id
        );

        this.updateRefererCookie( 'updated' );

        // Redirect. Do not clear store here so "Hakemuksella ei ole asuntoja" does not flash; logoutUser() clears before redirect.
        this.logoutUser();
      } else {

        // Favourites and fields are cleared when new application is submitted.
        this.resetLocalStorage();
        this.move("success");
      }
    },
    /**
     * Note when we are editing the application we want to reset only the current type.
     * There is also applicationReset.resetLocalStorage() that will be triggered when the application will be expired.
     * applicationReset version does empty all the application types not just the current type.
     */
    resetLocalStorage() {
      // Local storage update is disabled to make sure it stays empty.
      this.enableLocalStorageUpdate = false;
      this.store.emptyFavourites({ type: favouritesType });
      this.store.removeLocalSave({ type: favouritesType });
    },
    // Create a single object from current state containing everything that is saved to local storage.
    getLocalSaveFromState(full = false) {
      // Guard against Vue 3 initialization order issues where data() may not be ready yet
      if (!this.applicants || !this.form) {
        return null;
      }
      
      const pick = (obj, properties) =>
        Object.assign(...properties.map((key) => ({ [key]: obj[key] })));
      const extend = createExtend(localSaveExtend);
      return {
        applicants: this.applicants.map((applicant) => ({
          ...pick(applicant, [
            "FirstName",
            "LastName",
            "Ssn",
            "AddedNewApplicant",
            "Address",
            "PostalCode",
            "City",
            "usePrimaryApplicantAddress",
            "Phone",
            "PhoneCountryCode",
            "ContactLanguage",
            "Email",
            "emailAgain",
            "MarketingAllowed",
          ]),
          ...(full ? extend("localSaveFromStateApplicant", applicant) : {}),
        })),
        form: {
          Id: full ? this.form.Id : null,
          AdditionalWishes: this.form.AdditionalWishes,
          ASOAraOrderNumber: this.form.ASOAraOrderNumber,
          isARA: this.form.isARA,
          Checksum: this.form.Checksum,
          OtherResidents: (this.form.OtherResidents || []).map((otherResident) =>
            pick(otherResident, ["FirstName", "LastName", "Ssn"])
          ),
          ARACurrentAccommodationType: this.form.ARACurrentAccommodationType,
          ARACurrentMunicipality: this.form.ARACurrentMunicipality,
          ...(full ? extend("localSaveFromStateForm", this) : {}),
        },
        residentInLocation: this.residentInLocation,
        ...(full ? extend("localSaveFromState", this) : {}),
      };
    },
    setStateFromLocalSave(localSave, full = false) {
      this.applicants = localSave.applicants.map(this.createApplicant);
      this.initialApplicantsCount = this.applicants.length;
      this.form.OtherResidents = localSave.form.OtherResidents.map(
        this.createOtherResident
      );
      this.form.AdditionalWishes = localSave.form.AdditionalWishes;
      this.form.ASOAraOrderNumber = localSave.form.ASOAraOrderNumber;
      this.form.isARA = localSave.form.isARA;
      this.form.Checksum = localSave.form.Checksum;
      this.residentInLocation = localSave.residentInLocation;
      this.form.ARACurrentAccommodationType =
        localSave.form.ARACurrentAccommodationType;
      this.form.ARACurrentMunicipality = localSave.form.ARACurrentMunicipality;
      if (!full) {
        return;
      }
      this.form.Id = localSave.form.Id;
      const extend = createExtend(localSaveExtend);
      extend("stateFromLocalSave", this, localSave);
    },
    // !! DEPRECATED - The application data is not populated to the "preset" prop anymore.
    // Load state from API. There are a couple of optional hooks/mappers that are defined in presetMapExtend to
    // extend this logic for each component.
    async statePresetLoad() {
      const { preset } = this;
      const extend = createExtend(presetMapExtend);

      // For applicants, emailAgain field must be filled as it is not included in data.
      this.applicants = [
        preset.PrimaryApplicant,
        ...preset.OtherApplicants,
      ].map((applicant) =>
        this.createApplicant({
          ...applicant,
          emailAgain: applicant.Email || "",
          ...extend("mapApplicant", applicant),
        })
      );
      this.initialApplicantsCount = this.applicants.length;

      this.form.Id = preset.Id;
      this.form.OtherResidents = preset.OtherResidents.map(
        this.createOtherResident
      );
      this.form.AdditionalWishes = preset.AdditionalWishes;

      // A "hook" that can be implemented in the component.
      extend("hook", this);

      // Replace current favourites with apartments in preset data.
      this.store.emptyFavourites({ type: favouritesType });
      preset.SelectedApartments.forEach((apartment) => {
        this.store.addFavourite({
          uuid: apartment.Id,
          type: favouritesType,
        });
      });
      await this.store.requestAndCleanUpFavourites();

      // A "hook" that can be implemented in the component.
      extend("hookApartmentsLoaded", this);
    },
    async populateStateAndLocalStorageWithApplicationData(
      applicationData,
      applicationType
    ) {
      const data = Object.assign({}, applicationData); // Clone the object just to make sure we don't cause any unwanted side effects
      const extend = createExtend(presetMapExtend);

      this.applicants = [data.PrimaryApplicant, ...data.OtherApplicants].map(
        (applicant) => {
          try {
            return this.createApplicant({
              ...applicant,
              emailAgain: applicant.Email || "",
              ...extend("mapApplicant", applicant),
            });
          } catch (err) {
            console.log(err);
          }
        }
      );
      this.initialApplicantsCount = this.applicants.length;

      this.store.emptyFavourites();
      data.SelectedApartments.forEach((apartment) => {
        this.store.addFavourite({
          uuid: apartment.Id,
          type: favouritesType,
        });
      });
      await this.store.requestAndCleanUpFavourites();

      if (applicationType === "aso") {
        // When loading preset values from API, order numbers must be reassigned to cities. The state of
        // shareOrderNumber is not available in the preset.

        // 1. Remove all the apartments that we don't have the data for. It is possible (at least in theory) that
        // the data for a previously added apartment is no longer available.
        const selectedApartmentsFiltered = data.SelectedApartments.filter(
          (apartment) =>
            this.apartments
              .map((apartment) => apartment.mpc_asuntoid)
              .includes(apartment.Id)
        );

        // 2. Create a set of order numbers that contains all cityIds exactly once.
        const uniqueOrderNumbersWithApartmentIds =
          selectedApartmentsFiltered.reduce((accumulator, apartment) => {
            const hasOrderNumber = !!apartment.ASOOrderNumber;
            const cityId = this.apartments.find(
              (a) => a.mpc_asuntoid === apartment.Id
            ).mpc_kaupunki;
            return hasOrderNumber
              ? {
                  ...accumulator,
                  [cityId]: apartment.ASOOrderNumber,
                }
              : accumulator;
          }, {});

        // 3. Save the order number for each city
        Object.entries(uniqueOrderNumbersWithApartmentIds).forEach(
          ([cityId, orderNumber]) => {
            this.orderNumbers[cityId] = orderNumber.toString();
          }
        );
        // 4. Connect residency info to a locality (there can only be one)
        selectedApartmentsFiltered.forEach((apartment) => {
          if (apartment.Resident) {
            const localityId = this.apartments.find(
              (a) => a.mpc_asuntoid === apartment.Id
            ).mpc_kohdeid;
            //const address = this.apartments.find((a) => a.mpc_asuntoid === apartment.Id).building.mpc_katuosoite;
            const localityName = this.apartments.find(
              (a) => a.mpc_asuntoid === apartment.Id
            ).housing_locality.mpc_name;
            this.residentInLocation = {
              localityId: localityId,
              isResident: apartment.Resident,
              localityName: [localityName],
            };
          }
        });
        // 5. isARA
        this.form.isARA = data.isARA;
        this.form.ASOAraOrderNumber = data.ASOAraOrderNumber;
        this.form.ARACurrentMunicipality = data.ARACurrentMunicipality;
        this.form.ARACurrentAccommodationType =
          data.ARACurrentAccommodationType;
      } else if (applicationType === "owner-occupancy") {
        // When loading preset values from API, order numbers must be reassigned to cities. The state of
        // shareOrderNumber is not available in the preset.

        // 1. Remove all the apartments that we don't have the data for. It is possible (at least in theory) that
        // the data for a previously added apartment is no longer available.
        const selectedApartmentsFiltered = data.SelectedApartments.filter(
          (apartment) =>
            this.apartments
              .map((apartment) => apartment.mpc_asuntoid)
              .includes(apartment.Id)
        );

        // 2. Create a set of order numbers that contains all cityIds exactly once.
        const uniqueOrderNumbersWithApartmentIds =
          selectedApartmentsFiltered.reduce((accumulator, apartment) => {
            const hasOrderNumber = !!apartment.ASOOrderNumber;
            const cityId = this.apartments.find(
              (a) => a.mpc_asuntoid === apartment.Id
            ).mpc_kaupunki;
            return hasOrderNumber
              ? {
                  ...accumulator,
                  [cityId]: apartment.ASOOrderNumber,
                }
              : accumulator;
          }, {});

        // 3. Save the order number for each city
        Object.entries(uniqueOrderNumbersWithApartmentIds).forEach(
          ([cityId, orderNumber]) => {
            this.orderNumbers[cityId] = orderNumber.toString();
          }
        );
        // 4. Connect residency info to a locality (there can only be one)
        selectedApartmentsFiltered.forEach((apartment) => {
          if (apartment.Resident) {
            const localityId = this.apartments.find(
              (a) => a.mpc_asuntoid === apartment.Id
            ).mpc_kohdeid;
            //const address = this.apartments.find((a) => a.mpc_asuntoid === apartment.Id).building.mpc_katuosoite;
            const localityName = this.apartments.find(
              (a) => a.mpc_asuntoid === apartment.Id
            ).housing_locality.mpc_name;
            this.residentInLocation = {
              localityId: localityId,
              isResident: apartment.Resident,
              localityName: [localityName],
            };
          }
        });
        // 5. isARA
        this.form.isARA = data.isARA;
        this.form.ASOAraOrderNumber = data.ASOAraOrderNumber;
        this.form.ARACurrentMunicipality = data.ARACurrentMunicipality;
        this.form.ARACurrentAccommodationType =
          data.ARACurrentAccommodationType;
      } else if (applicationType === "rent") {
        this.form.TotalWealth = `${data.TotalWealth}`; // Cast to string
        this.form.ApplicationReason = data.ApplicationReason;
        this.form.NeedForApartmentStartDate = data.NeedForApartmentStartDate;
        this.form.CurrentApartmentInfo = data.CurrentApartmentInfo;
        this.form.ApplicationReasonAdditionalInfo =
          data.ApplicationReasonAdditionalInfo;
      }

      this.form.Id = data.Id;
      this.form.OtherResidents = data.OtherResidents.map(
        this.createOtherResident
      );
      this.form.AdditionalWishes = data.AdditionalWishes;
      extend("hook", this);

      this.form.Checksum = data.Checksum;
      this.enableLocalStorageUpdate = true;
      this.store.storeLocalSave({
        type: favouritesType,
        localSave: this.computedLocalSave,
      });

      // A "hook" that can be implemented in the component.
      extend("hookApartmentsLoaded", this);
    },
    createApplicant: extendApplicantCreator(),
    createOtherResident: (presets = {}) => ({
      id: unique(),
      Ssn: "",
      LastName: "",
      FirstName: "",
      ...presets,
    }),
    createFormApartment: (apartment) => {
      return {
        Id: apartment.mpc_asuntoid,
        CampaignCode: null,
      };
    },

    /**
     * Redirect and logout user from OIDC and site.
     * We want to call this with await e.g. await this.logoutUser(); and handle the return value.
     * For state handling I recommend using property like this.userLoggedOut (bool).
     *
     * @returns {Promise<boolean>}
     */
    async logoutUser () {

      const logoutFailedUrl = globals.urlHome + globals.userLogoutFailedEndpoint;

      try {
        const logoutEndpoint = globals.urlHome + '/wp-json/avainasunnot/v1/logout';

        // Execution pauses until the request is done
        const response = await axios.post(logoutEndpoint);
        const logoutUrl = response.data;

        if ( logoutUrl ) {

          this.resetLocalStorage();
          console.log('Local storage cleared!');

          // Redirect to OIDC logout.
          window.location.href = logoutUrl;
        }
        else {
          console.log( 'Logout failed' );

          const logoutFailedUrl = createURLWithParams(
            window.location.href,
            {
              'state': 'logout-failed'
            }
          );

          window.location.href = logoutFailedUrl;
          return false;
        }

        return true;

      } catch (error) {
        console.error('There was an error while logging out the user!', error);
        window.location.href = logoutFailedUrl;
        return false;
      }
    },
    updateRefererCookie( state ) {
      /**
       * If user has navigated to different view from the application view
       * the ?application param will be lost.
       * Here we add application param if needed.
       */
      const applicationUrlWithApplicationParam = maybeAddQueryParam(
        window.location.href,
        'application',
        this.form.Id
      );

      const refererUrlUpdated = createURLWithParams(
        applicationUrlWithApplicationParam,
        {
          'state': state
        }
      );
      updateCookieWithString('referer_cookie', refererUrlUpdated,null, globals.refererCookieSettings );
    },
  }, // Methods ends.
  async beforeMount() {
    // Note: Vuelidate is initialized in created() hook (see above)
    // v$ should be ready at this point
    console.log('[APPLICATION-DEBUG] beforeMount - v$ is:', this.v$ ? 'READY' : 'NOT READY');
    
    // Shared data in local storage is always removed.
    const localSaveSharedRaw = localStorage.getItem("applicationShared");
    if (localSaveSharedRaw) {
      localStorage.removeItem("applicationShared");
    }

    // A custom reset handler is used when a preset application is present to prevent application re-initializing itself
    // immediately after a reset by reloading the URL.
    applicationReset.setHandler((maybeResetLocalStorage) => {

      this.enableLocalStorageUpdate = false;
      maybeResetLocalStorage();
      this.navigateAfterReset();

      this.updateRefererCookie( 'logged-out' );

      this.logoutUser();
    });

    // There are four different cases that can happen when initializing the application. Note that the order these are
    // checked has significance here: preset will *always* be prioritized over any of the other options etc.

    // 1) Display the "Continue your application" modal and load the existing application data to the form
    // When: User is coming in from a "Continue your application" link - i.e. "application" GET parameter exists in the URL
    // What: Present a modal. The modal asks for the SSN of the Primary Applicant. If the SSN the user enters matches the
    //       SSN in the application data, the server will return the application data. If not, the server will return an error message.
    // NOTE: the logic can continue to the next step even if the modal is loaded - the application state is ONLY reset with the API data
    //       if the user enters a valid SSN to the modal. The user also has an option to hide the modal.
    const queryParams = utils.generalHelpers.methods.getJSONFromURL();

    // "application" might cause problems in localhost, therefore also allow "applicationlocal"
    if (queryParams.application || queryParams.applicationlocal) {
      this.showModal.prefillApplication = true;
    }

    // 2) Application in progress
    // When: There is full application data (application[*]) present in local storage AND if preset data was passed as
    // props, its Id value matches the one in local storage.
    // What: All fields are initialized from the data in local storage.
    const localSave = this.store.localSave[favouritesType];
    if (localSave) {
      this.setStateFromLocalSave(localSave, true);
      await this.store.requestAndCleanUpFavourites();
      this.enableLocalStorageUpdate = true;
      return;
    }

    // !! DISABLED 2021-05
    // 2) Load existing application
    // When: Preset data was passed as props.
    // What: Fields are loaded from preset. Favourites are overwritten with the ones in preset data. A local save is
    // created immediately.
    // if (this.preset) {
    //   await this.statePresetLoad();
    //   this.enableLocalStorageUpdate = true;
    //   this.$store.commit('localSave/store', {type: favouritesType, localSave: this.computedLocalSave});
    //   this.showModal.loadPreset = this.modalLoadPresetEnabled;
    //   return;
    // }

    // 3) Start from shared data
    // When: There is shared data (applicationShared) present in local storage.
    // What: Shared fields are initialized from the data in local storage. Application-specific fields are empty. A
    // local save is created immediately.
    if (localSaveSharedRaw) {
      const localSaveShared = JSON.parse(localSaveSharedRaw);
      this.setStateFromLocalSave(localSaveShared);
      await this.store.requestAndCleanUpFavourites();
      this.enableLocalStorageUpdate = true;
      this.store.storeLocalSave({
        type: favouritesType,
        localSave: this.computedLocalSave,
      });
      return;
    }

    // 4) Fresh start
    // When: Default behaviour.
    // What: All fields are empty. Local save is not created immediately - only when the user actually fills something
    // in the application.
    await this.store.requestAndCleanUpFavourites();
    this.enableLocalStorageUpdate = true;
  },
});
